import { Box } from '@mui/material';
import type { GridRowParams } from '@mui/x-data-grid';
import { useCallback, useState } from 'react';
import type { FC } from 'react';

import { Table } from '@components/Table';
import type { TableProps } from '@components/Table';
import { VrsAccountFormDialogContainer } from '@components/VrsAccountForm';
import { useVrsAccounts } from '@hooks/api';
import { useColumnVisibilityModel } from '@hooks/useColumnVisibilityModel';
import { useTableFilterSort } from '@hooks/useTableFilterSort';
import type { VrsAccount } from 'types/api';

import { VrsAccountsTableSearchFilter } from './SearchFilter';
import * as classes from './VrsAccountsTable.module.css';
import {
  columns,
  initialTableFilterSortArgs,
  columnVisibilityModelDesktop,
  columnVisibilityModelMobile,
} from './constants';

export interface VrsAccountsTableProps {
  syncWithQueryParams?: boolean;
}

export const VrsAccountsTable: FC<VrsAccountsTableProps> = ({ syncWithQueryParams = false }) => {
  const [vrsAccountId, setVrsAccountId] = useState<string | undefined>(undefined);
  const tableFilterSort = useTableFilterSort(syncWithQueryParams)(initialTableFilterSortArgs);
  const { filters, page, size, setFilters, sort } = tableFilterSort;
  const { data, error, isLoading, mutate } = useVrsAccounts(
    {
      filters,
      page,
      size,
      sort,
    },
    {
      refreshInterval: 60000,
    },
  );
  const columnVisibilityModel = useColumnVisibilityModel(
    columnVisibilityModelMobile,
    columnVisibilityModelDesktop,
  );

  const onRowClick: TableProps['onRowClick'] = useCallback(
    (rowData: GridRowParams<VrsAccount>) => {
      setVrsAccountId(rowData.row.id);
    },
    [setVrsAccountId],
  );

  const onClose = useCallback(() => {
    setVrsAccountId(undefined);
    mutate();
  }, [setVrsAccountId, mutate]);

  return (
    <div className={classes.container}>
      <VrsAccountFormDialogContainer onClose={onClose} vrsAccountId={vrsAccountId} />
      <Box marginBottom={2} marginTop={2}>
        <VrsAccountsTableSearchFilter initialValues={filters} onChange={setFilters} />
      </Box>
      <Table
        columnVisibilityModel={columnVisibilityModel}
        columns={columns}
        disableSelectionOnClick
        error={error as Error}
        loading={isLoading}
        localStorageSettingsKey="vrs-accounts-table-settings"
        onRowClick={onRowClick}
        rowCount={data?.totalElements}
        rows={data?.content}
        tableFilterSort={tableFilterSort}
      />
    </div>
  );
};
